<template>
	<ManageLinkLayout
		:current-step.sync="currentStep"
		:steps.sync="steps"
		:is-edit="isEdit"
		:is-enabled="link.isEnabled"
		:link-types="lookUpData.linkTypes"
		:link-type="linkType"
		:back-button-action="goBackToOverview"
		:finish-button-action="finish"
		:link-has-changed="linkHasChanged"
		:brand-id="isEdit ? link.brandId : userBrands[0]"
		:link-name="link.linkName"
		:link="link"
		:accessed-via-hub="accessedViaHub"
		:record-added="recordAdded"
		@switch-to-edit="switchToEdit"
		@update-steps="updateSteps"
	>
		<template #content>
			<PreferenceLink
				:link.sync="link"
				:step="currentStep"
				:steps="steps"
				:look-up-data="lookUpData"
				:link-type="linkType"
				:embed-code="embedCode"
				:new-link-id="newLinkId"
				:assigned-language-items="assignedLanguageItems"
				:clone-mode="cloneMode"
				@reload-look-up-data="loadLinks"
				@extended-prefs-changed="extendedPrefsChanged"
			/>
		</template>
	</ManageLinkLayout>
</template>
<script>
import { mapGetters } from 'vuex'
import PreferenceLink from './preference-link.vue'
import { CONSENT_COLLECTION, PREFERENCE_PAGE_LINKS,	ACTION_LINKS } from '../../../../router/route-names.js'
import { getLinks, upsertActionLink, upsertPreferencePageLink } from '../../../../../../shared/utils/api/admin-portal/links/links.js'
import ManageLinkLayout from '../manage-link-layout.vue'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { brandOptions, defaultBrandInSelect } from '../../../../../../shared/state/brands.js'
import { updatePrivacyPolicies } from '../../../../../../shared/state/privacy-policies.js'
import { linkTypes } from './link-types.js'

export default {
	components: { ManageLinkLayout, PreferenceLink },
	props: {
		linkToEdit: Object,
		linkType: {
			type: Number,
			required: true
		},
		assignedLanguageItems: {
			type: Array,
			default: () => []
		},
		cloneMode: {
			type: Boolean,
			default: false
		},
		accessedViaHub: {
			type: Boolean,
			default: false
		}
	},
	setup () {
		return {
			brandOptions,
			updatePrivacyPolicies,
			defaultBrandInSelect
		}
	},
	data () {
		const preferencePageLink = this.linkType === linkTypes.preferencePageLink
		return {
			currentStep: 0,
			link: {
				...(this.linkToEdit || { linkFields: [{}], customPrivacyPolicies: [], brandId: defaultBrandInSelect.value, customDefaultLanguageId: 0 })
			},
			lookUpData: {},
			actionLinks: {},
			preferencePageLinks: {},
			embedCode: '',
			newLinkId: null,
			currentLinkType: null,
			extendedPreferencesChanged: false,
			steps: [],
			preferencePageLink,
			recordAdded: null,
			newLink: null,
			switchedToEdit: false
		}
	},
	computed: {
		...mapGetters('auth', ['userBrands']),
		isEdit () {
			return !!this.linkToEdit || this.switchedToEdit
		},
		linkHasChanged () {
			return JSON.stringify(this.linkToEdit) !== JSON.stringify(this.link) || this.extendedPreferencesChanged || this.cloneMode
		}
	},
	watch: {
		'link.enableOneClickUnsubscribe': {
			handler (newValue) {
				this.setSteps()
				// When enabling One-Click Unsubscribe manually via toggle, ensure configurationOptions defaults are set
				if (newValue && !this.link.configurationOptions?.successOutcome) {
					this.link.configurationOptions = {
						...this.link.configurationOptions,
						createStanddakIfNoMatch: this.link.configurationOptions?.createStanddakIfNoMatch ?? false,
						multiStandDakMatchIsFailure: this.link.configurationOptions?.multiStandDakMatchIsFailure ?? false,
						ipLimitations: {
							...this.link.configurationOptions?.ipLimitations,
							noOfSubmissionsAllowedFromIp: this.link.configurationOptions?.ipLimitations?.noOfSubmissionsAllowedFromIp ?? 0,
							ipLimitPeriodInMins: this.link.configurationOptions?.ipLimitations?.ipLimitPeriodInMins ?? 0,
							limitSubmissionsByIpTypeId: this.link.configurationOptions?.ipLimitations?.limitSubmissionsByIpTypeId ?? false
						},
						successOutcome: {
							confirmationPageTypeId: 3, // Remain on Page (default for OCU)
							externalConfirmationPageUrl: null,
							internalConfirmationPageId: null,
							failureResponsePageId: null
						}
					}
				}
			}
		}
	},
	async created () {
		// Check for One-Click Unsubscribe query param and pre-set Direct Auth
		if (this.$route.query.isOneClickUnsubscribe === 'true') {
			this.link.enableOneClickUnsubscribe = true
			// Pre-set Direct Authentication (type 1) for One-Click Unsubscribe links
			this.link.authenticationDetails = {
				...this.link.authenticationDetails,
				authTypeId: 1
			}
			// Pre-set default configurationOptions since Outcomes step is skipped for OCU links
			this.link.configurationOptions = {
				...this.link.configurationOptions,
				createStanddakIfNoMatch: this.link.configurationOptions?.createStanddakIfNoMatch ?? false,
				multiStandDakMatchIsFailure: this.link.configurationOptions?.multiStandDakMatchIsFailure ?? false,
				ipLimitations: {
					...this.link.configurationOptions?.ipLimitations,
					noOfSubmissionsAllowedFromIp: this.link.configurationOptions?.ipLimitations?.noOfSubmissionsAllowedFromIp ?? 0,
					ipLimitPeriodInMins: this.link.configurationOptions?.ipLimitations?.ipLimitPeriodInMins ?? 0,
					limitSubmissionsByIpTypeId: this.link.configurationOptions?.ipLimitations?.limitSubmissionsByIpTypeId ?? false
				},
				successOutcome: {
					confirmationPageTypeId: 3, // Remain on Page (default for OCU)
					externalConfirmationPageUrl: null,
					internalConfirmationPageId: null,
					failureResponsePageId: null
				}
			}
		}

		this.setSteps()

		const DEFAULT_BRAND = 0
		await this.loadLinks()
		this.currentLinkType = this.$route.query.linkType
		if (!this.isEdit) {
			this.link.brandId = DEFAULT_BRAND
			if (this.brandOptions.length === 1) {
				this.link.brandId = this.brandOptions[0]?.value ?? DEFAULT_BRAND
			}
		} else {
			if (this.currentLinkType === linkTypes.actionLink) {
				this.embedCode = this.linkToEdit.actionLinkEmbedCode
			} else if (this.currentLinkType === linkTypes.preferencePageLink) {
				this.embedCode = this.linkToEdit.preferencePageLinkEmbedCode
			}
			this.setAllStepsToComplete()
		}
		updatePrivacyPolicies()
	},
	methods: {
		setSteps () {
			const preferencePageLink = this.linkType === linkTypes.preferencePageLink
			const isOneClickUnsubscribe = this.link.enableOneClickUnsubscribe === true

			let steps = [
				{ title: 'Details', stepComplete: false },
				{ title: 'Fields', stepComplete: false },
				{ title: 'Preferences', stepComplete: false },
				{ title: 'Personal Information Fields', stepComplete: false },
				{ title: 'Properties', stepComplete: false },
				{ title: 'Outcomes', stepComplete: false },
				{ title: 'Themes', stepComplete: false },
				{ title: 'Link', stepComplete: false }
			]

			// Filter out Personal Information Fields for non-preference page links
			if (!preferencePageLink) {
				steps = steps.filter(({ title }) => title !== 'Personal Information Fields')
			}

			// Filter out Outcomes and Themes for One-Click Unsubscribe links
			if (isOneClickUnsubscribe) {
				steps = steps.filter(({ title }) => title !== 'Outcomes' && title !== 'Themes')
			}

			// Re-number steps after filtering
			this.steps = steps.map((step, idx) => ({
				...step,
				slot: idx,
				stepNumber: idx
			}))
		},
		async loadLinks () {
			const { data: { lookUpData, actionLinks, preferencePageLinks } } = await getLinks()
			this.lookUpData = lookUpData
			this.actionLinks = actionLinks
			this.preferencePageLinks = preferencePageLinks
		},
		goBackToOverview () {
			if (this.accessedViaHub) {
				this.$router.push(CONSENT_COLLECTION)
			} else {
				this.$router.push({
					name: this.currentLinkType === linkTypes.actionLink ? ACTION_LINKS : PREFERENCE_PAGE_LINKS
				})
			}
		},
		extendedPrefsChanged () {
			this.extendedPreferencesChanged = true
		},
		async setNewLinkEmbedCode () {
			await this.loadLinks()

			if (this.currentLinkType === linkTypes.actionLink) {
				this.newLink = Object.assign({}, this.actionLinks.find(link => link.linkId === this.newLinkId))
				const linkSnippet = this.newLink?.actionLinkEmbedCode
				this.embedCode = linkSnippet
			} else if (this.currentLinkType === linkTypes.preferencePageLink) {
				this.newLink = Object.assign({}, this.preferencePageLinks.find(link => link.linkId === this.newLinkId))
				const linkSnippet = this.newLink?.preferencePageLinkEmbedCode
				this.embedCode = linkSnippet
			}
		},
		updateSteps () {
			this.steps[this.currentStep].stepComplete = true
		},
		setAllStepsToComplete () {
			this.steps.forEach(step => {
				step.stepComplete = true
			})
		},
		async finish () {
			let addingNewRecord = false
			if (!this.isEdit && !(this.link.actionLinkId || this.link.preferencePageLinkId)) {
				addingNewRecord = true
			}
			if (this.link.linkFields.filter(({ isEncrypted }) => isEncrypted).length === 0) {
				this.link.configurationOptions.encryption = {
					encryptionKeyId: 0,
					encryptionKeyName: null
				}
			}
			if (this.currentLinkType === linkTypes.actionLink) {
				if (this.$route.params.linkToEdit) {
					this.link.actionLinkId = this.$route.params.linkToEdit.linkId
				} else if (this.switchedToEdit) {
					this.link.actionLinkId = this.link.linkId
				}
				// When cloning, ensure we update the newly created record after the initial insert
				if (this.cloneMode && this.newLinkId) {
					// Persist previously created cloned id so subsequent saves are updates
					this.link.actionLinkId = this.newLinkId
					this.link.linkId = this.newLinkId
				}
				await upsertActionLink(this.link).then(response => {
					if (!this.isEdit || this.cloneMode) {
						const createdId = response.data.newActionLinkId
						if (Number.isInteger(createdId) && createdId > 0) {
							this.newLinkId = createdId
						}
						if (this.cloneMode) {
							// Immediately switch context to editing the new cloned record
							this.link.linkId = this.newLinkId
							this.link.actionLinkId = this.newLinkId
							this.switchedToEdit = true
						}
						this.setNewLinkEmbedCode()
					}
				})
			} else if (this.currentLinkType === linkTypes.preferencePageLink) {
				if (this.$route.params.linkToEdit) {
					this.link.preferencePageLinkId = this.$route.params.linkToEdit.linkId
				} else if (this.switchedToEdit) {
					this.link.preferencePageLinkId = this.link.linkId
				}
				// When cloning, ensure subsequent saves reference the newly created preference page link
				if (this.cloneMode && this.newLinkId) {
					this.link.preferencePageLinkId = this.newLinkId
					this.link.linkId = this.newLinkId
				}
				if (this.link.contactDetailsFields) {
					this.link.contactDetailsFields = this.link.contactDetailsFields.map((contactField, index) => ({
						...contactField,
						displayOrder: index + 1
					}))
				}
				await upsertPreferencePageLink(this.link).then(response => {
					if (!this.isEdit || this.cloneMode) {
						const createdId = response.data.newPreferencePageLinkId
						if (Number.isInteger(createdId) && createdId > 0) {
							this.newLinkId = createdId
						}
						if (this.cloneMode) {
							// After initial clone insert, set ids so future saves update
							this.link.linkId = this.newLinkId
							this.link.preferencePageLinkId = this.newLinkId
							this.switchedToEdit = true
						}
						this.setNewLinkEmbedCode()
					}
				})
				if (this.link.preferencePageLinkConfig.extendedPrefs) {
					let linkWithNewExtendedPreferenceDates = this.link.preferencePageLinkConfig.extendedPrefs
					linkWithNewExtendedPreferenceDates = linkWithNewExtendedPreferenceDates.map(extPref => ({
						...extPref,
						dateCreated: extPref.dateCreated ? extPref.dateCreated : new Date()
					}))
					this.link.preferencePageLinkConfig.extendedPrefs = linkWithNewExtendedPreferenceDates
				}
			}
			showSnackbar(`This link has been ${this.isEdit ? 'updated' : 'added'}`)
			if (addingNewRecord) {
				this.recordAdded = true
			}
		},
		switchToEdit () {
			this.link = { ...this.newLink }
			if (this.currentLinkType === linkTypes.actionLink) {
				this.embedCode = this.link.actionLinkEmbedCode
			} else if (this.currentLinkType === linkTypes.preferencePageLink) {
				this.embedCode = this.link.preferencePageLinkEmbedCode
			}
			this.newLink = null
			this.recordAdded = false
			this.switchedToEdit = true
		}
	}
}
</script>
